import { Build, Component, type ComponentInterface, Element, Host, Prop, State, Watch, h } from '@stencil/core';
import { type Attributes, getAssetByPath, inheritAriaAttributes } from '~/util/DOM';
import { loadSvgContent, ltcIconsContent } from './request';

@Component({
  tag: 'ltc-icon',
  assetsDirs: ['svgs'],
  styleUrl: 'icon.less',
  shadow: true,
})
export class LtcIcon implements ComponentInterface {
  /**
   * 1. Own Properties
   */
  private io?: IntersectionObserver;
  private inheritedAttributes: Attributes = {};
  private didLoadIcon = false;

  /**
   * 2. Reference to host HTML element.
   */
  @Element() host!: HTMLLtcIconElement;

  /**
   * 3. State() variables
   */
  @State() private svgContent?: string;
  @State() private isVisible = false;

  /**
   * 4. Public Property API
   */

  /**
   * The color to use for the icon. This is optional. By default, the icon adopts the font color.
   */
  @Prop({ reflect: true }) color?: string;

  /**
   * Specifies which icon to use from the built-in set of icons.
   */
  @Prop({ reflect: true }) name!: string;

  /**
   * The size of the icon. 16 / 24 / 32 / 48 px.
   * This is optional. By default, the icon adopts the font size.
   */
  @Prop({ reflect: true }) size?: 'tiny' | 'small' | 'medium' | 'large';

  /**
   * If enabled, ltc-icon will be loaded lazily when it's visible in the viewport.
   * Default, `false`.
   */
  @Prop({ reflect: true }) lazy = false;

  /**
   * Prop lifecycle events
   */
  @Watch('name')
  loadIcon() {
    if (Build.isBrowser && this.isVisible) {
      const url = getAssetByPath(`svgs/${this.name}.svg`);

      if (url) {
        if (ltcIconsContent.has(url)) {
          // sync if it's already loaded
          this.svgContent = ltcIconsContent.get(url);
        } else {
          // async if it hasn't been loaded
          loadSvgContent(url).then(() => {
            this.svgContent = ltcIconsContent.get(url);
          });
        }
        this.didLoadIcon = true;
      }
    }
  }

  /**
   * 5. Events section
   */

  /**
   * 6. Component lifecycle events
   */
  componentWillLoad() {
    this.inheritedAttributes = inheritAriaAttributes(this.host);
  }

  connectedCallback() {
    // purposely do not return the promise here because loading
    // the svg file should not hold up loading the app
    // only load the svg if it's visible
    this.waitUntilVisible(this.host, '50px', () => {
      this.isVisible = true;
      this.loadIcon();
    });
  }

  componentDidLoad() {
    if (!this.didLoadIcon) {
      this.loadIcon();
    }
  }

  disconnectedCallback() {
    if (this.io) {
      this.io.disconnect();
      this.io = undefined;
    }
  }

  /**
   * 7. Listeners
   */

  /**
   * 8. Public methods API
   */

  /**
   * 9. Local methods
   */

  waitUntilVisible(el: HTMLElement, rootMargin: string, cb: () => void) {
    if (Build.isBrowser && this.lazy && typeof window !== 'undefined' && window.IntersectionObserver) {
      const io = new window.IntersectionObserver(
        (data: IntersectionObserverEntry[]) => {
          if (data[0].isIntersecting) {
            io.disconnect();
            this.io = undefined;
            cb();
          }
        },
        { rootMargin },
      );

      io.observe(el);

      this.io = io;
    } else {
      // browser doesn't support IntersectionObserver
      // so just fallback to always show it
      cb();
    }
  }

  /**
   * 10. render() function
   */
  render() {
    const { inheritedAttributes } = this;

    return (
      <Host
        role="img"
        class={{
          [`icon-${this.size}`]: !!this.size,
        }}
        {...inheritedAttributes}
      >
        {Build.isBrowser && this.svgContent ? (
          <div class="icon-inner" style={{ color: this.color }} innerHTML={this.svgContent} />
        ) : (
          <div class="icon-inner">
            <svg />
          </div>
        )}
      </Host>
    );
  }
}
